import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';
import { useAuth, hasRole } from '@/lib/auth';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Plus, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

const STATUSES = [
  { key: 'open', es: 'Abierto', en: 'Open', color: 'border-red-500/40 bg-red-500/10' },
  { key: 'investigating', es: 'Investigando', en: 'Investigating', color: 'border-amber-500/40 bg-amber-500/10' },
  { key: 'mitigating', es: 'Mitigando', en: 'Mitigating', color: 'border-sky-500/40 bg-sky-500/10' },
  { key: 'resolved', es: 'Resuelto', en: 'Resolved', color: 'border-emerald-500/40 bg-emerald-500/10' },
  { key: 'closed', es: 'Cerrado', en: 'Closed', color: 'border-slate-500/40 bg-slate-500/10' },
];

const SEV_CLS = {
  low: 'bg-slate-500/15 text-slate-300',
  medium: 'bg-sky-500/15 text-sky-300',
  high: 'bg-amber-500/15 text-amber-300',
  critical: 'bg-red-500/15 text-red-300',
};

export default function IncidentsPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const canEdit = hasRole(user, ['admin', 'tech']);
  const [items, setItems] = useState([]);
  const [services, setServices] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [comment, setComment] = useState('');
  const [form, setForm] = useState({ title: '', description: '', service_id: '', severity: 'medium', status: 'open' });

  const load = async () => {
    const [{ data: list }, { data: svcs }] = await Promise.all([api.get('/incidents'), api.get('/services')]);
    setItems(list); setServices(svcs);
  };
  useEffect(() => { load(); if (hasRole(user, ['admin'])) api.get('/users').then(({ data }) => setUsers(data)).catch(() => {}); }, [user]);

  const create = async () => {
    try {
      await api.post('/incidents', { ...form, service_id: form.service_id || null });
      toast.success(t('success'));
      setOpen(false);
      setForm({ title: '', description: '', service_id: '', severity: 'medium', status: 'open' });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || t('error')); }
  };

  const updateStatus = async (id, status) => {
    try { await api.patch(`/incidents/${id}`, { status }); load(); if (selected?.id === id) setSelected({ ...selected, status }); }
    catch (e) { toast.error(t('error')); }
  };

  const addComment = async () => {
    if (!comment.trim()) return;
    await api.post(`/incidents/${selected.id}/comments`, { body: comment });
    setComment('');
    const { data } = await api.get(`/incidents/${selected.id}`);
    setSelected(data);
  };

  const grouped = STATUSES.reduce((acc, s) => { acc[s.key] = items.filter((i) => i.status === s.key); return acc; }, {});

  return (
    <div className="space-y-6" data-testid="incidents-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold">{t('incidents_title')}</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">{items.length} total</p>
        </div>
        {canEdit && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="incidents-create-button"><Plus className="h-4 w-4 mr-2" /> {t('incidents_new')}</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>{t('incidents_new')}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="space-y-1"><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="incident-form-title" /></div>
                <div className="space-y-1"><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1"><Label>{t('incidents_severity')}</Label>
                    <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
                      <SelectTrigger data-testid="incident-form-severity"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {['low', 'medium', 'high', 'critical'].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1"><Label>{t('logs_service')}</Label>
                    <Select value={form.service_id} onValueChange={(v) => setForm({ ...form, service_id: v })}>
                      <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        {services.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
              <DialogFooter><Button variant="ghost" onClick={() => setOpen(false)}>{t('cancel')}</Button><Button onClick={create} data-testid="incident-form-submit">{t('create')}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3" data-testid="incidents-board">
        {STATUSES.map((s) => (
          <Card key={s.key} className="p-3 bg-card border border-border min-h-[300px]">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs uppercase tracking-wide text-[hsl(var(--muted-foreground))]">{i18n.language === 'en' ? s.en : s.es}</div>
              <span className={`text-xs px-2 py-0.5 rounded-full border ${s.color}`}>{grouped[s.key].length}</span>
            </div>
            <div className="space-y-2">
              {grouped[s.key].map((i) => (
                <button
                  key={i.id} onClick={() => setSelected(i)}
                  className="w-full text-left p-3 rounded-lg border border-border bg-[hsl(var(--muted))] hover:border-[hsl(var(--primary)/.4)] transition-colors duration-150"
                  data-testid={`incident-card-${i.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="text-sm font-medium leading-snug">{i.title}</div>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${SEV_CLS[i.severity]}`}>{i.severity}</span>
                  </div>
                  <div className="mt-2 flex items-center justify-between text-[11px] text-[hsl(var(--muted-foreground))]">
                    <span className="font-mono">{services.find((sv) => sv.id === i.service_id)?.name || '—'}</span>
                    <span>{i.created_at ? formatDistanceToNow(new Date(i.created_at), { addSuffix: true }) : ''}</span>
                  </div>
                </button>
              ))}
            </div>
          </Card>
        ))}
      </div>

      <Sheet open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <SheetContent className="w-[480px] sm:max-w-[480px] overflow-y-auto">
          {selected && (
            <div>
              <SheetHeader><SheetTitle>{selected.title}</SheetTitle></SheetHeader>
              <div className="mt-4 space-y-3">
                <div className="flex items-center gap-2"><span className={`text-xs px-2 py-0.5 rounded ${SEV_CLS[selected.severity]}`}>{selected.severity}</span></div>
                <div className="text-sm text-[hsl(var(--muted-foreground))]">{selected.description || '—'}</div>
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-wide">Status</Label>
                  <Select value={selected.status} onValueChange={(v) => updateStatus(selected.id, v)}>
                    <SelectTrigger data-testid={`incident-status-change-${selected.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {STATUSES.map((s) => <SelectItem key={s.key} value={s.key}>{i18n.language === 'en' ? s.en : s.es}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-2">Comments</div>
                  <ul className="space-y-2">
                    {(selected.comments || []).map((c) => (
                      <li key={c.id} className="p-2 rounded border border-border bg-[hsl(var(--muted))]">
                        <div className="text-xs text-[hsl(var(--muted-foreground))] flex justify-between"><span>{c.author_name}</span><span>{c.created_at ? formatDistanceToNow(new Date(c.created_at), { addSuffix: true }) : ''}</span></div>
                        <div className="text-sm">{c.body}</div>
                      </li>
                    ))}
                  </ul>
                  {canEdit && (
                    <div className="mt-2 flex gap-2">
                      <Input value={comment} onChange={(e) => setComment(e.target.value)} placeholder="..." data-testid="incident-comment-input" />
                      <Button size="sm" onClick={addComment} data-testid="incident-comment-submit"><MessageSquare className="h-3.5 w-3.5" /></Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
